import logger from "@next/core-logger"
import {IncomingHttpHeaders} from "http"
import type State from "ducks/state"
import {type Store} from "@reduxjs/toolkit"
import {getPrimaryNavData} from "../../api/primary-nav"
import {PrimaryNavItem, DefaultConfig, AlignmentValues} from "../../models/primary-nav"
import {
    DEFAULT_PRIMARY_NAV_VERSION,
    SNAIL_TRAIL_CONTAINER,
    PRIMARY_NAV_ALIGNMENT_TYPES,
    TABBED_NAVIGATION_API_QUERY_PARAMETER_NAME,
    TABBED_NAVIGATION_COOKIE_TABBED_VALUE,
} from "../../config/constants"
import {reorderPinnedElements} from "../../utils/personalisePrimaryNav"
import isSecurePageRequested from "../../utils/secureHeader"
import {getSecondaryNavDataThunk, setIsInSecondaryMeganav} from "../secondary-nav"
import {SupportedRegionTypes} from "../../models/regions"

export const SET_PRIMARY_NAV_ACTIVE_INDEX = "SET_PRIMARY_NAV_ACTIVE_INDEX"
export const SET_PRIMARY_NAV_DATA = "SET_PRIMARY_NAV_DATA"
export const SET_IS_IN_PRIMARY_NAV = "SET_IS_IN_PRIMARY_NAV"
export const SET_DEFAULT_PRIMARY_CONFIG = "SET_DEFAULT_PRIMARY_CONFIG"
export const SET_PRIMARY_NAV_ALIGNMENT = "SET_PRIMARY_NAV_ALIGNMENT"

export type PrimaryNavDuckState = {
    active: boolean
    activeDepartment: string
    activeDepartmentIndex: number
    alignment?: string
    config: DefaultConfig
    isInPrimaryNav: boolean
    items: PrimaryNavItem[]
}

export interface Action {
    type:
        | typeof SET_PRIMARY_NAV_ACTIVE_INDEX
        | typeof SET_PRIMARY_NAV_DATA
        | typeof SET_IS_IN_PRIMARY_NAV
        | typeof SET_DEFAULT_PRIMARY_CONFIG
        | typeof SET_PRIMARY_NAV_ALIGNMENT
    payload: Partial<PrimaryNavDuckState>
}

export interface ActiveIndex {
    type: typeof SET_PRIMARY_NAV_ACTIVE_INDEX
    payload: {
        active: boolean
        activeDepartment: string
        activeDepartmentIndex: number
    }
}
export interface NavData {
    type: typeof SET_PRIMARY_NAV_DATA
    payload: PrimaryNavItem[]
}
export interface Alignment {
    type: typeof SET_PRIMARY_NAV_ALIGNMENT
    payload: AlignmentValues
}
export interface IsInPrimaryNav {
    type: typeof SET_IS_IN_PRIMARY_NAV
    payload: boolean
}
export interface DefaultVersion {
    type: typeof SET_DEFAULT_PRIMARY_CONFIG
    payload: DefaultConfig
}

export const initialState: PrimaryNavDuckState = {
    active: false,
    activeDepartment: "",
    activeDepartmentIndex: -1,
    alignment: "evenly-spread",
    config: {
        version: DEFAULT_PRIMARY_NAV_VERSION,
        country: "",
    },
    isInPrimaryNav: false,
    items: [],
}

export const setDefaultConfig = (store: Store, config: DefaultConfig) =>
    store.dispatch({
        type: SET_DEFAULT_PRIMARY_CONFIG,
        payload: config,
    })

export const setIsInPrimaryNav = (isInPrimaryNav: boolean) => dispatch =>
    dispatch({
        type: SET_IS_IN_PRIMARY_NAV,
        payload: isInPrimaryNav,
    })

export const setPrimaryNavIndex = (activeDepartmentIndex: number, activeDepartment: string) => dispatch =>
    dispatch({
        type: SET_PRIMARY_NAV_ACTIVE_INDEX,
        payload: {
            activeDepartment: activeDepartment.toLowerCase(),
            activeDepartmentIndex,
            active: activeDepartmentIndex > -1,
        },
    })

export const handleAlignment = (alignment: AlignmentValues | string | undefined | null) => {
    return alignment ? (alignment.trim().toLowerCase() as AlignmentValues) : PRIMARY_NAV_ALIGNMENT_TYPES.EVENLY_SPREAD
}

// We use display value to determine if the next department hidden because PersonalisePrimaryNav shuffles and hides
// primary navigation elements in client side rendering.
export const getDepartmentStatus = (index: number) => {
    const element = document.querySelector(`[id='${SNAIL_TRAIL_CONTAINER}'] > [data-index='${index}']`) as HTMLElement
    return {
        hidden: element && window.getComputedStyle(element, null).display === "none",
        index: Number(element.dataset.index),
    }
}

const closeSecondaryNav = dispatch => {
    dispatch(setPrimaryNavIndex(-1, ""))
    dispatch(setIsInSecondaryMeganav(false))
    dispatch(setIsInPrimaryNav(false))
}

const shouldCloseSecondaryNav = (isNext, nextIndex, itemsLength) => {
    const canNavigateNext = nextIndex < itemsLength
    const canNavigateBack = nextIndex >= 0
    if (isNext && canNavigateNext) {
        return false
    }
    if (!isNext && canNavigateBack) {
        return false
    }
    return true
}

export const navigate = (direction: "next" | "back") => {
    return (dispatch, getState) => {
        const primaryNavContainer = document.getElementById(SNAIL_TRAIL_CONTAINER)
        if (primaryNavContainer) {
            const state = getState() as State
            const isNext = direction === "next"
            let nextIndex = isNext
                ? state.primarynav.activeDepartmentIndex + 1
                : state.primarynav.activeDepartmentIndex - 1

            if (shouldCloseSecondaryNav(isNext, nextIndex, state.primarynav.items.length)) {
                closeSecondaryNav(dispatch)
                return
            }

            let nextDepartmentIndex = getDepartmentStatus(nextIndex).index
            while (getDepartmentStatus(nextIndex).hidden) {
                nextIndex = isNext ? nextIndex + 1 : nextIndex - 1
                if (shouldCloseSecondaryNav(isNext, nextIndex, state.primarynav.items.length)) {
                    closeSecondaryNav(dispatch)
                    return
                }
                nextDepartmentIndex = getDepartmentStatus(nextIndex).index
            }

            const nextDepartment = state.primarynav.items[nextDepartmentIndex]
            if (nextDepartment) {
                dispatch(setPrimaryNavIndex(nextIndex, nextDepartment.title))
                dispatch(getSecondaryNavDataThunk(nextDepartment.path))
            }
        }
    }
}

export const getQueryParameters = (getState: () => any) => {
    const state = getState()
    const {
        settings: {tabbedNavigationEnabled},
        request: {headers},
    } = state
    const tabbedNavHeader = (headers && headers["x-next-tabbed-navigation"]) || ""
    if (!tabbedNavigationEnabled || tabbedNavHeader !== TABBED_NAVIGATION_COOKIE_TABBED_VALUE) return ``
    const menuTab: any = getState().data?.regions.find(region => region.type === SupportedRegionTypes.TabbedDepartment)
    if (menuTab) {
        const {name} = menuTab.elements[0]
        return `${TABBED_NAVIGATION_API_QUERY_PARAMETER_NAME}=${name}`
    }
    return ""
}

export const getPrimaryNavDataThunk = async ({dispatch, getState}: Store) => {
    try {
        const {
            request,
            primarynav: {config},
            settings: {secureHeaderEnabled},
        } = getState() as State

        // if secure header, no primary nav needed
        if (isSecurePageRequested(request.url || "") && secureHeaderEnabled) return

        const queryParameters = getQueryParameters(getState)
        const response = await getPrimaryNavData(request.headers as IncomingHttpHeaders, config, queryParameters)

        dispatch({
            type: SET_PRIMARY_NAV_DATA,
            payload: reorderPinnedElements(response.items) || [],
        })
        dispatch({
            type: SET_PRIMARY_NAV_ALIGNMENT,
            payload: handleAlignment(response.alignment),
        })
    } catch (error) {
        logger.error(error)
        dispatch({type: SET_PRIMARY_NAV_DATA, payload: []})
        dispatch({type: SET_PRIMARY_NAV_ALIGNMENT, payload: PRIMARY_NAV_ALIGNMENT_TYPES.EVENLY_SPREAD})
    }
}

const reducer = (
    state: PrimaryNavDuckState = initialState,
    action: ActiveIndex | NavData | Alignment | IsInPrimaryNav | DefaultVersion,
): PrimaryNavDuckState => {
    switch (action.type) {
        case SET_PRIMARY_NAV_ACTIVE_INDEX:
            return {
                ...state,
                ...action.payload,
            }
        case SET_PRIMARY_NAV_DATA:
            return {
                ...state,
                items: action.payload,
            }
        case SET_PRIMARY_NAV_ALIGNMENT:
            return {
                ...state,
                alignment: action.payload,
            }
        case SET_IS_IN_PRIMARY_NAV:
            return {
                ...state,
                isInPrimaryNav: action.payload,
            }
        case SET_DEFAULT_PRIMARY_CONFIG:
            return {
                ...state,
                config: action.payload,
            }
        default:
            return state
    }
}

export default reducer
